import { useEffect, useCallback, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import {
  queueGetAll,
  queueRemove,
  queueIncrementRetry,
  queueCount,
  QueuedOperation,
} from '@/utils/offlineQueue';

// Allowlist of tables the offline queue is permitted to write to.
// Names must match actual Supabase table names exactly.
// Never derive this from localStorage-controlled data.
const ALLOWED_TABLES = new Set([
  'vaccines',
  'medications',
  'appointments',
  'pet_weights',
  'health_anti_parasitic',
  'health_hygiene',
  'pet_dental_care',
  'pet_hoof_care',
  'pet_injury_records',
  'pet_food_records',
  'pet_temperature_logs',
  'pet_humidity_logs',
  'health_allergies',
  'health_diseases',
  'health_surgeries',
]);

// Tables that have no user_id column — RLS enforces ownership via pet_id.
// For these tables, we must NOT inject user_id into insert/update payloads.
const TABLES_WITHOUT_USER_ID = new Set(['vaccines', 'medications']);

const ALLOWED_OP_TYPES = new Set(['insert', 'update', 'delete']);

async function executeOperation(op: QueuedOperation, sessionUserId: string): Promise<boolean> {
  // Validate against allowlists — never trust localStorage-sourced table/type
  if (!ALLOWED_TABLES.has(op.table)) {
    logger.error(`Offline queue: blocked write to unauthorized table "${op.table}"`);
    return false;
  }
  if (!ALLOWED_OP_TYPES.has(op.type)) {
    logger.error(`Offline queue: blocked unknown op type "${op.type}"`);
    return false;
  }

  const hasUserIdCol = !TABLES_WITHOUT_USER_ID.has(op.table);

  try {
    if (op.type === 'insert') {
      const payload = hasUserIdCol
        ? { ...op.payload, user_id: sessionUserId }
        : op.payload;
      const { error } = await supabase.from(op.table as never).insert(payload as never);
      return !error;
    }
    if (op.type === 'update' && op.recordId) {
      let q = supabase
        .from(op.table as never)
        .update(op.payload as never)
        .eq('id', op.recordId);
      if (hasUserIdCol) q = q.eq('user_id', sessionUserId);
      const { error } = await q;
      return !error;
    }
    if (op.type === 'delete' && op.recordId) {
      let q = supabase.from(op.table as never).delete().eq('id', op.recordId);
      if (hasUserIdCol) q = q.eq('user_id', sessionUserId);
      const { error } = await q;
      return !error;
    }
    return false;
  } catch {
    return false;
  }
}

export function useOfflineSync() {
  const { user } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  const updateCount = useCallback(() => {
    if (user) setPendingCount(queueCount(user.id));
  }, [user]);

  const flushQueue = useCallback(async () => {
    if (!user || isSyncing || !navigator.onLine) return;

    const queue = queueGetAll(user.id);
    if (queue.length === 0) return;

    setIsSyncing(true);
    let succeeded = 0;
    let failed = 0;

    for (const op of queue) {
      const ok = await executeOperation(op, user.id);
      if (ok) {
        queueRemove(user.id, op.id);
        succeeded++;
      } else {
        queueIncrementRetry(user.id, op.id);
        failed++;
        logger.error('Offline op replay failed:', { table: op.table, type: op.type, id: op.id });
      }
    }

    setIsSyncing(false);
    updateCount();

    if (succeeded > 0) {
      toast.success(`${succeeded} modification${succeeded > 1 ? 's' : ''} synchronisée${succeeded > 1 ? 's' : ''}`);
    }
    if (failed > 0) {
      toast.error(`${failed} opération${failed > 1 ? 's' : ''} non synchronisée${failed > 1 ? 's' : ''} — réessai au prochain accès`);
    }
  }, [user, isSyncing, updateCount]);

  // Flush when coming back online
  useEffect(() => {
    window.addEventListener('online', flushQueue);
    return () => window.removeEventListener('online', flushQueue);
  }, [flushQueue]);

  // Initial count on mount
  useEffect(() => {
    updateCount();
  }, [updateCount]);

  // Attempt flush on mount if already online and queue non-empty
  useEffect(() => {
    if (navigator.onLine) flushQueue();
  }, [flushQueue]);

  return { pendingCount, isSyncing, flushQueue };
}
